import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, MessageCircle, Smartphone, Instagram, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/track";

const MOOD_CHALLENGES = [
    "What are you done explaining?",
    "What is your current mood?",
    "What is your healing era?",
    "What are you choosing peace over?",
    "What is your Sunday mood?",
    "What is your villain arc line?",
];

const ChallengeButton = ({ children, onClick, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className="text-left w-full rounded-xl border border-app/20 bg-surface-1 hover:bg-surface-2 hover:border-app/40 transition-colors px-4 py-3"
    >
        <span className="font-serif italic text-app text-[15px] leading-snug">{children}</span>
    </button>
);

/**
 * ShareAfter — lightweight, non-blocking viral prompt shown AFTER a user
 * downloads or shares a card. Lets them invite friends with a mood challenge.
 */
export default function ShareAfter({ open, onOpenChange, shareUrl, caption }) {
    const [copied, setCopied] = React.useState(false);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            track("copy_link_clicked", { source: "share_after" });
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            console.error(e);
        }
    };

    const sendChallenge = (target, prompt) => {
        track("mood_challenge_sent", { target, prompt });
        // Format: emotional line + strong CTA + identity hashtags + URL preview
        const msg = caption
            ? `${prompt}\n\nmine: ${caption}`
            : `${prompt}\n\nyour turn → ${shareUrl} #MoodCard #BuiltDifferent`;
        const enc = encodeURIComponent(msg);
        if (target === "whatsapp") {
            window.open(`https://wa.me/?text=${enc}`, "_blank", "noopener,noreferrer");
        } else if (target === "sms") {
            window.location.href = `sms:?&body=${enc}`;
        } else if (target === "instagram") {
            navigator.clipboard.writeText(msg).catch(() => {});
            toast.success("Copied — paste in Instagram DM");
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => onOpenChange(false)}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
                        data-testid="share-after-backdrop"
                    />
                    <motion.div
                        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bottom-0 left-0 right-0 z-50 max-w-md mx-auto"
                        data-testid="share-after-sheet"
                    >
                        <div className="relative bg-app-2 border-t border-x border-app/30 rounded-t-3xl shadow-[0_-20px_60px_rgba(0,0,0,0.6)] overflow-hidden max-h-[88vh] flex flex-col">
                            <div className="absolute inset-0 grain pointer-events-none" />
                            <div className="relative px-6 pt-5 pb-2 shrink-0">
                                <div className="mx-auto w-10 h-1 rounded-full bg-surface-3 mb-4" />
                                <div className="flex items-start justify-between">
                                    <div>
                                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">saved · shared</span>
                                        <h3 className="mt-1 font-serif font-light italic text-app text-2xl tracking-tight">
                                            want to see what your friends are feeling?
                                        </h3>
                                    </div>
                                    <button
                                        onClick={() => onOpenChange(false)}
                                        data-testid="share-after-close"
                                        className="p-2 rounded-full text-app-3 hover:text-app hover:bg-surface-2 transition-colors"
                                    >
                                        <X className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>

                            <div className="relative flex-1 overflow-y-auto no-scrollbar px-6 pb-5">
                                {/* Quick actions */}
                                <div className="grid grid-cols-4 gap-2 mt-3 mb-5">
                                    <button
                                        onClick={copyLink}
                                        data-testid="share-after-copy"
                                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-app/20 bg-surface-1 hover:bg-surface-2 transition-colors"
                                    >
                                        {copied ? <Check className="w-4 h-4 text-app" /> : <Copy className="w-4 h-4 text-app" strokeWidth={1.6} />}
                                        <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-app-2">Copy</span>
                                    </button>
                                    <button
                                        onClick={() => sendChallenge("whatsapp", "what's your current mood?")}
                                        data-testid="share-after-whatsapp"
                                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-app/20 bg-surface-1 hover:bg-surface-2 transition-colors"
                                    >
                                        <MessageCircle className="w-4 h-4 text-app" strokeWidth={1.6} />
                                        <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-app-2">WA</span>
                                    </button>
                                    <button
                                        onClick={() => sendChallenge("sms", "what's your current mood?")}
                                        data-testid="share-after-sms"
                                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-app/20 bg-surface-1 hover:bg-surface-2 transition-colors"
                                    >
                                        <Smartphone className="w-4 h-4 text-app" strokeWidth={1.6} />
                                        <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-app-2">SMS</span>
                                    </button>
                                    <button
                                        onClick={() => sendChallenge("instagram", "what's your current mood?")}
                                        data-testid="share-after-ig"
                                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl border border-app/20 bg-surface-1 hover:bg-surface-2 transition-colors"
                                    >
                                        <Instagram className="w-4 h-4 text-app" strokeWidth={1.6} />
                                        <span className="font-sans text-[10px] uppercase tracking-[0.25em] text-app-2">IG DM</span>
                                    </button>
                                </div>

                                {/* Mood challenges */}
                                <div className="mb-3 flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">send a mood challenge</span>
                                </div>
                                <div className="space-y-2">
                                    {MOOD_CHALLENGES.map((p) => (
                                        <ChallengeButton
                                            key={p}
                                            testid={`mood-challenge-${p.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "").slice(0, 32)}`}
                                            onClick={() => sendChallenge("whatsapp", p)}
                                        >
                                            {p}
                                        </ChallengeButton>
                                    ))}
                                </div>
                                <p className="mt-4 text-center font-serif italic text-[11px] text-app-3">
                                    one tap. no signup needed for them either.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
